import React, { useEffect, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { CardVariant, KnowledgeSmartCard } from '@atlassian/lumina';

import { useAIAnswerContext } from '../../../../../ai-answer-dialog/ai-answer-context';
import { useKnowledgeSmartCardAnalytics } from '../../../../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesStack } from '../../../../../common/ui/sources-stack/sources-stack';
import type { NLPSearchResultSource } from '../../../../types';

const messages = defineMessages({
	inline_citations_group_title: {
		id: 'search_ai.inline_citations_group_title',
		defaultMessage:
			'{numCitations, plural, one {{numCitations, number} Source} other {{numCitations, number} Sources}}',
		description: 'Inline citations group title in dropdown showing the count',
	},
	inline_citations_close_dropdown_label: {
		id: 'search_ai.inline_citations_close_dropdown_label',
		defaultMessage: 'Close the dropdown',
		description: 'Label for button to close the citations dropdown',
	},
});

const styles = cssMap({
	citationGroup: {
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.075'),
		borderRadius: token('radius.medium'),
		verticalAlign: 'text-bottom',
		marginTop: token('space.negative.050'),
		marginBottom: token('space.negative.050'),
	},
	citationDropdownContainer: {
		width: '400px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a[data-smart-element="Title"]': {
			fontWeight: token('font.weight.medium'),
			color: token('color.text'),
		},
	},
	citationDropdownHeader: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
});

export interface InlineCitationGroupProps {
	parsedSources: NLPSearchResultSource[];
}

export const InlineCitationGroup = ({
	parsedSources,
}: InlineCitationGroupProps): React.JSX.Element => {
	const [isCitationsOpen, setIsCitationsOpen] = useState(false);
	// ref to keep track of whether user opens dropdown by clicking
	const dropdownOpenByClick = useRef<boolean>(false);
	const closeDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const { formatMessage } = useIntl();

	useEffect(() => {
		return () => {
			if (closeDropdownTimeoutRef.current) {
				clearTimeout(closeDropdownTimeoutRef.current);
			}
		};
	}, []);

	const context = useAIAnswerContext();
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
		isCitation: true,
	});

	const handlePressableClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		// only close if user had originally opened dropdown by clicking
		// without this, it causes unwanted behavior with the hover behavior
		if (isCitationsOpen && dropdownOpenByClick.current) {
			setIsCitationsOpen(false);
		}
		dropdownOpenByClick.current = !dropdownOpenByClick.current;
	};

	// set timer to close to avoid closing when user is moving mouse
	// between pressable and dropdown (small space in between)
	const handlePressableMouseLeave = () => {
		closeDropdownTimeoutRef.current = setTimeout(() => {
			if (!dropdownOpenByClick.current) {
				setIsCitationsOpen(false);
			}
		}, 150);
	};

	// clear timer above once moving mouse to dropdown
	const handleDropdownMouseEnter = () => {
		if (closeDropdownTimeoutRef.current) {
			clearTimeout(closeDropdownTimeoutRef.current);
		}
	};

	// prevent closing dropdown if user had originally opened dropdown by clicking
	const handleDropdownMouseLeave = () => {
		if (!dropdownOpenByClick.current) {
			setIsCitationsOpen(false);
		}
		return;
	};

	// callback for when user clicks outside of trigger pressable component
	const handleDropdownOpenChange = (attrs: OnOpenChangeArgs) => {
		if (!attrs.isOpen) {
			dropdownOpenByClick.current = false;
		}
		setIsCitationsOpen(attrs.isOpen);
	};

	const handleButtonClose = () => {
		dropdownOpenByClick.current = false;
		setIsCitationsOpen(false);
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Pressable
					ref={triggerRef}
					{...props}
					xcss={styles.citationGroup}
					onClick={handlePressableClick}
					onMouseEnter={() => setIsCitationsOpen(true)}
					onMouseLeave={handlePressableMouseLeave}
					testId="smart-answer-inline-citation-group"
				>
					<Inline space="space.050" alignBlock="center">
						<SourcesStack
							sources={parsedSources}
							maxSourcesInStack={2}
							showNumAddtlSources
							addEditorStyleOverrides
						/>
					</Inline>
				</Pressable>
			)}
			shouldFlip
			isOpen={isCitationsOpen}
			onOpenChange={handleDropdownOpenChange}
		>
			<Box
				onMouseEnter={handleDropdownMouseEnter}
				onMouseLeave={handleDropdownMouseLeave}
				xcss={styles.citationDropdownContainer}
				testId="smart-answer-inline-citation-dropdown"
			>
				<Box xcss={styles.citationDropdownHeader}>
					<Inline alignBlock="center" spread="space-between">
						<Heading size="xsmall">
							{formatMessage(messages.inline_citations_group_title, {
								numCitations: parsedSources.length,
							})}
						</Heading>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(messages.inline_citations_close_dropdown_label)}
							onClick={handleButtonClose}
							spacing="compact"
							testId="smart-answer-inline-citation-dropdown-close-button"
						/>
					</Inline>
				</Box>
				{parsedSources.map((source, index) => (
					<KnowledgeSmartCard
						source={source}
						variant={CardVariant.DROPDOWN_SOURCE}
						index={index}
						searchSessionId={searchSessionId}
						onClickHandler={handleSmartCardClick}
					/>
				))}
			</Box>
		</DropdownMenu>
	);
};
